import type { RopCard3002CentroDeOperacoesSolarEventosDosAtivos } from "./ropCard3002CentroDeOperacoesSolarEventosDosAtivos.types";

export type InverterOperationalStatus =
  | "generating"
  | "failure"
  | "communicationFailure"
  | "performance"
  | "available";

export type InverterNotificationType =
  | "critical"
  | "performance"
  | "communication"
  | "generating"
  | "available";

export type InverterNotification = {
  type: InverterNotificationType;
  title: string;
  items: string[];
};

export type InverterMonitorAggregatedData =
  RopCard3002CentroDeOperacoesSolarEventosDosAtivos["ropCard25InvertersMonitorAggregatedCollection"];

export type InverterStatusViewModel = {
  key: InverterOperationalStatus;
  label: string;
  total: number;
  inverters: string[];
  description: string;
  color: string;
  notificationType: InverterNotificationType;
};

function getTotalWithoutCommunicationInverters(
  data: InverterMonitorAggregatedData,
): number {
  return (
    data.totalwithoutCommunicationInverters ??
    data.totalWithoutCommunicationInverters ??
    0
  );
}

export function getInverterStatusSum(data: InverterMonitorAggregatedData): number {
  return (
    (data.totalGeneratingInverters ?? 0) +
    (data.totalOfflineInverters ?? 0) +
    getTotalWithoutCommunicationInverters(data) +
    (data.totalPerformanceInverters ?? 0) +
    (data.totalAvailableInverters ?? 0)
  );
}

export function assertInverterStatusInvariant(
  data: InverterMonitorAggregatedData,
): void {
  const sum = getInverterStatusSum(data);
  if (sum !== data.totalInverters) {
    throw new Error(
      `Inverter status sum (${sum}) does not match totalInverters (${data.totalInverters})`,
    );
  }
}

export function mapInverterOperationalStatus(
  data: InverterMonitorAggregatedData,
): InverterStatusViewModel[] {
  return [
    {
      key: "generating",
      label: "Gerando",
      total: data.totalGeneratingInverters ?? 0,
      inverters: data.generatingInverters ?? [],
      description: "Inversor em operação com geração ativa.",
      color: "success.main",
      notificationType: "generating",
    },
    {
      key: "failure",
      label: "Em Falha",
      total: data.totalOfflineInverters ?? 0,
      inverters: data.offlineInverters ?? [],
      description: "Inversor com alarme de falha ativo.",
      color: "error.main",
      notificationType: "critical",
    },
    {
      key: "communicationFailure",
      label: "Falha de Comunicação",
      total: getTotalWithoutCommunicationInverters(data),
      inverters: data.withoutCommunicationInverters ?? [],
      description:
        "Inversor com falha de comunicação ou sem telemetria válida.",
      color: "#ba68c8",
      notificationType: "communication",
    },
    {
      key: "performance",
      label: "Performance",
      total: data.totalPerformanceInverters ?? 0,
      inverters: data.performanceInverters ?? [],
      description: "Inversor gerando abaixo do desempenho esperado.",
      color: "warning.main",
      notificationType: "performance",
    },
    {
      key: "available",
      label: "Disponível",
      total: data.totalAvailableInverters ?? 0,
      inverters: data.availableInverters ?? [],
      description: "Inversor sem falhas, porém sem geração",
      color: "info.main",
      notificationType: "available",
    },
  ];
}

export function mapInverterNotifications(
  statuses: InverterStatusViewModel[],
): InverterNotification[] {
  return statuses
    .filter((status) => status.inverters.length > 0)
    .map((status) => ({
      type: status.notificationType,
      title: status.label,
      items: status.inverters,
    }));
}

export function getInverterStatusTotal(
  statuses: InverterStatusViewModel[],
  key: InverterOperationalStatus,
): number {
  return statuses.find((status) => status.key === key)?.total ?? 0;
}
